/**
 * All game audio is synthesized with WebAudio — no asset files.
 * init() must be called from a user gesture to unlock the context.
 */
export class ProceduralAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  // engine drone nodes
  private engineOsc: OscillatorNode | null = null;
  private engineOsc2: OscillatorNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private engineGain: GainNode | null = null;

  async init(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return;
    }
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);

    // shared noise source material
    const seconds = 1.5;
    this.noiseBuffer = this.ctx.createBuffer(1, this.ctx.sampleRate * seconds, this.ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }

  startEngine(): void {
    if (!this.ctx || !this.master || this.engineOsc) return;

    this.engineOsc = this.ctx.createOscillator();
    this.engineOsc.type = 'sawtooth';
    this.engineOsc.frequency.value = 55;

    this.engineOsc2 = this.ctx.createOscillator();
    this.engineOsc2.type = 'square';
    this.engineOsc2.frequency.value = 28;

    this.engineFilter = this.ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 500;

    this.engineGain = this.ctx.createGain();
    this.engineGain.gain.value = 0;

    this.engineOsc.connect(this.engineFilter);
    this.engineOsc2.connect(this.engineFilter);
    this.engineFilter.connect(this.engineGain);
    this.engineGain.connect(this.master);

    this.engineOsc.start();
    this.engineOsc2.start();
  }

  /** speed01: 0 (idle) .. 1 (boost) */
  setEngine(speed01: number): void {
    if (!this.ctx || !this.engineOsc || !this.engineOsc2 || !this.engineFilter || !this.engineGain) return;
    const t = this.ctx.currentTime;
    this.engineOsc.frequency.setTargetAtTime(50 + speed01 * 95, t, 0.1);
    this.engineOsc2.frequency.setTargetAtTime(25 + speed01 * 48, t, 0.1);
    this.engineFilter.frequency.setTargetAtTime(380 + speed01 * 900, t, 0.1);
    this.engineGain.gain.setTargetAtTime(0.05 + speed01 * 0.05, t, 0.15);
  }

  stopEngine(): void {
    if (!this.ctx || !this.engineGain) return;
    this.engineGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.2);
  }

  shoot(): void {
    this.noiseBurst(0.07, 1600 + Math.random() * 500, 'bandpass', 0.22);
    this.blip('square', 200, 90, 0.03, 0.1);
  }

  partnerShoot(): void {
    this.noiseBurst(0.06, 1200, 'bandpass', 0.12);
  }

  enemyShoot(): void {
    this.noiseBurst(0.09, 700, 'bandpass', 0.16);
    this.blip('sawtooth', 130, 60, 0.08, 0.12);
  }

  hit(): void {
    this.blip('sine', 1250, 850, 0.05, 0.12);
  }

  explosion(big = false): void {
    if (!this.ctx || !this.master || !this.noiseBuffer) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    const t = this.ctx.currentTime;
    filter.frequency.setValueAtTime(big ? 1400 : 900, t);
    filter.frequency.exponentialRampToValueAtTime(70, t + (big ? 1.0 : 0.6));

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(big ? 0.65 : 0.42, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + (big ? 1.1 : 0.65));

    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    src.start(t, Math.random() * 0.5);
    src.stop(t + 1.2);

    this.blip('sine', big ? 70 : 90, 40, big ? 0.5 : 0.3, 0.35);
  }

  vehicleHit(): void {
    this.blip('sine', 95, 45, 0.22, 0.4);
    this.noiseBurst(0.12, 300, 'lowpass', 0.25);
  }

  countdownBeep(final: boolean): void {
    this.blip('square', final ? 990 : 660, final ? 990 : 660, final ? 0.25 : 0.1, 0.14);
  }

  uiClick(): void {
    this.blip('sine', 500, 700, 0.04, 0.08);
  }

  private blip(
    type: OscillatorType,
    fromHz: number,
    toHz: number,
    duration: number,
    volume: number
  ): void {
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(fromHz, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, toHz), t + duration);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(gain);
    gain.connect(this.master);
    osc.start(t);
    osc.stop(t + duration + 0.02);
  }

  private noiseBurst(
    duration: number,
    filterHz: number,
    filterType: BiquadFilterType,
    volume: number
  ): void {
    if (!this.ctx || !this.master || !this.noiseBuffer) return;
    const t = this.ctx.currentTime;

    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = filterHz;
    filter.Q.value = 1.2;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    src.start(t, Math.random() * 0.8);
    src.stop(t + duration + 0.02);
  }
}
